/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { LifProtoCol } from 'app/common/constants';
import { ProductConfig, SWITCH } from 'app/config';
import { FeatureConfig } from 'app/config/feature';
import * as moment from 'moment';

import { type GrammarList } from '../conditionalSearch/grammar.type';

const assign = (objA, objB): any => {
    const apiKeys = Object.keys(objB);
    if (process.env.NODE_ENV === 'development') {
        const apiKeyA = Object.keys(objA);
        if (apiKeyA.length !== apiKeys.length || apiKeyA.some((key) => !apiKeys.includes(key))) {
            throw new Error('you can not batch because their has different types');
        }
    }
    const assignResult = {};
    apiKeys.forEach((key) => {
        const arrB = objB[key];
        const arrA = objA[key];
        assignResult[key] = arrB.map((i) => ({ ...arrA.find((ob) => ob.value === i.value), ...i }));
    });
    return assignResult;
};

/**
 * 该配置项在匹配时候会统一转换为大写形式
 * 请务必保持key值为全大写，否则会出错，匹配不到对应的url
 */
export enum DEFAULT_URL {
    'LUNS' = 'lun.getBatch',
    'LUNGROUPS' = 'lunGroup.getBatch',
    'NamespaceS' = 'lun.getBatch',
    'NamespaceGROUPS' = 'lunGroup.getBatch',
    'HOSTS' = 'host.getBatch',
    'HOSTGROUPS' = 'hostGroup.getBatch',
    'PORTGROUPS' = 'portGroup.getBatch',
    'LOGICALPORTS' = 'lif.getBatch',
    'VSTORES' = 'vstore.getBatch',
    'LUNSNAPSHOTS' = 'snapshot.getSnapshot',
    'FILESYSTEMS' = 'fileSystem.getBatch'
}

export enum PRECISION_TYPE {
    'LUNS' = 'lun',
    'LUNGROUPS' = 'lunGroup',
    'NamespaceS' = 'Namespace',
    'NamespaceGROUPS' = 'NamespaceGroup',
    'HOSTS' = 'host',
    'HOSTGROUPS' = 'hostGroup',
    'PORTGROUPS' = 'portGroup',
    'LOGICALPORTS' = 'logicPort',
    'VSTORES' = 'tenant',
    'LUNSNAPSHOTS' = 'lunSnapShot',
    'FILESYSTEMS' = 'fileSystems'
}

const directions = {
    LUN: 'LUNS',
    LUN组: 'LUNGROUPS',
    Namespace: 'NamespaceS',
    Namespace组: 'NamespaceGROUPS',
    NAMESPACES: 'NamespaceS',
    NAMESPACEGROUPS: 'NamespaceGROUPS',
    主机: 'HOSTS',
    主机组: 'HOSTGROUPS',
    端口组: 'PORTGROUPS',
    逻辑端口: 'LOGICALPORTS',
    多租户: 'VSTORES',
    LUN快照: 'LUNSNAPSHOTS',
    文件系统: 'FILESYSTEMS',
};
/**
 * 该配置项在匹配时候会统一转换为大写形式
 * 请务必保持key值为全大写，否则会出错，导致管道condition 无法获取正确的值
 */
const performanceGrammar = {
    'performanceData.getTopBatch': [{
        value: 22,
        type: 'performance',
        $default: 'ON',
        enumValue: { top: 'TOP15', bottom: 'BOTTOM15' },
        allowSymbol: ['ON'],
        index: 1,
    }, {
        value: 21,
        type: 'performance',
        $default: 'ON',
        enumValue: { top: 'TOP15', bottom: 'BOTTOM15' },
        allowSymbol: ['ON'],
        index: 1,
    }, {
        value: 370,
        type: 'performance',
        $default: 'ON',
        enumValue: { top: 'TOP15', bottom: 'BOTTOM15' },
        allowSymbol: ['ON'],
        index: 1,
    }],
};
const fileSystemPerformanceGrammar = {
    'performanceData.getTopBatch': [{
        value: 523,
        type: 'performance',
        $default: 'ON',
        enumValue: { top: 'TOP15', bottom: 'BOTTOM15' },
        allowSymbol: ['ON'],
        index: 1,
    }, {
        value: 511,
        type: 'performance',
        $default: 'ON',
        enumValue: { top: 'TOP15', bottom: 'BOTTOM15' },
        allowSymbol: ['ON'],
        index: 1,
    }, {
        value: 182,
        type: 'performance',
        $default: 'ON',
        enumValue: { top: 'TOP15', bottom: 'BOTTOM15' },
        allowSymbol: ['ON'],
        index: 1,
    }],
};
const LunGrammar = {
    'lun.getBatch': [
        {
            value: 'NAME',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
            index: 2,
        }, {
            value: 'CAPACITY',
            type: 'number',
            allowSymbol: ['>', '<', '>=', '<='],
            unit: 'Sector',
            tip: 'GB',
        }, {
            value: 'ALLOCCAPACITY',
            type: 'number',
            allowSymbol: ['>', '<', '>=', '<='],
            unit: 'Sector',
            tip: 'GB',
        }, {
            value: 'HEALTHSTATUS',
            type: 'enum',
            $default: '=',
            enumValue: { 1: '正常', 2: '故障', 15: '写保护' },
            allowSymbol: ['=', '!='],
        }, {
            value: 'RUNNINGSTATUS',
            type: 'enum',
            $default: '=',
            enumValue: { 27: '在线', 28: '离线' },
            allowSymbol: ['=', '!='],
        },
        // 微存储屏蔽功能类型
        ...(FeatureConfig.IsHideLunTableBusinessColumnDef ? [] : [
            {
                value: 'functionType',
                type: 'enum',
                $default: '=',
                enumValue: { 1: 'LUN', 2: '快照', 3: '克隆' },
                allowSymbol: ['=', '!='],
            },
        ]), {
            value: FeatureConfig.IsHideLunTableBusinessColumnDef ? 'EXPOSEDTOINITIATOR' : 'mapped',
            type: 'boolean',
            $default: '=',
            allowSymbol: ['='],
        }, {
            value: 'WWN',
            type: 'string',
            map: ['=', ':'],
            allowSymbol: ['='],
            $default: '=',
            validator: (value): boolean => /^[0-9A-Za-z]+$/g.test(value),
        },
    ],
    // 微存储屏蔽性能监控
    ...(FeatureConfig.IsHidePms ? {} : performanceGrammar),
};
const LunGroupGrammar = {
    'lunGroup.getBatch': [
        {
            value: 'NAME',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
            index: 2,
        }, {
            value: 'CAPCITY',
            type: 'number',
            allowSymbol: ['>', '<', '>=', '<='],
            unit: 'Sector',
            tip: 'GB',
        }, {
            value: 'allocatedCapacity',
            type: 'number',
            allowSymbol: ['>', '<', '>=', '<='],
            unit: 'Sector',
            tip: 'GB',
        }, {
            value: 'lunNumber',
            type: 'number',
            allowSymbol: ['>', '<', '>=', '<='],
        }, {
            value: 'ISADD2MAPPINGVIEW',
            type: 'boolean',
            $default: '=',
            allowSymbol: ['='],
        },
    ],
    // 微存储屏蔽性能监控
    ...(FeatureConfig.IsHidePms ? {} : performanceGrammar),
};
const HostGrammar = {
    'host.getBatch': [
        {
            value: 'NAME',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
            index: 2,
        },
        {
            value: 'HEALTHSTATUS',
            type: 'enum',
            $default: '=',
            enumValue: { 1: '正常', 2: '故障', 17: '无冗余路径', 18: '离线' },
            allowSymbol: ['=', '!='],

        },
        {
            value: 'OPERATIONSYSTEM',
            type: 'enum',
            $default: '=',
            enumValue: {
                0: 'Linux',
                1: 'Windows',
                2: 'Solaris',
                3: 'HP-UX',
                4: 'AIX',
                5: 'XenServer',
                6: 'Mac_OS',
                7: 'VMware_ESX',
                8: 'LINUX_VIS',
                // 9: 'Windows_Server_2012',
                // 10: 'Oracle_VM',
                11: 'OpenVMS',
                12: 'Oracle_VM_Server_for_x86',
                13: 'Oracle_VM_Server_for_SPARC',
            },
            allowSymbol: ['=', '!='],
        },
        {
            value: 'capacity',
            type: 'number',
            allowSymbol: ['>', '<', '>=', '<='],
            unit: 'Sector',
            tip: 'GB',
        },
        {
            value: 'allocatedCapacity',
            type: 'number',
            allowSymbol: ['>', '<', '>=', '<='],
            unit: 'Sector',
            tip: 'GB',
        },
        {
            value: 'PARENTNAME',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
        },
        {
            value: 'IP',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
        },
        {
            value: 'INITIATORNUM',
            type: 'number',
            allowSymbol: ['>', '<', '>=', '<='],
        },
    ],
    // 微存储屏蔽性能监控
    ...(FeatureConfig.IsHidePms ? {} : performanceGrammar),
};
const HostGroupGrammar = {
    'hostGroup.getBatch': [
        {
            value: 'NAME',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
            index: 2,
        },
        {
            value: 'capacity',
            type: 'number',
            allowSymbol: ['>', '<', '>=', '<='],
            unit: 'Sector',
            tip: 'GB',
        },
        {
            value: 'allocatedCapacity',
            type: 'number',
            allowSymbol: ['>', '<', '>=', '<='],
            unit: 'Sector',
            tip: 'GB',
        },
        {
            value: 'hostNumbe',
            type: 'number',
            allowSymbol: ['>', '<', '>=', '<='],
        },
    ],
    // 微存储屏蔽性能监控
    ...(FeatureConfig.IsHidePms ? {} : performanceGrammar),
};
const PortGroupGrammar = {
    'portGroup.getBatch': [
        {
            value: 'NAME',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
            index: 2,
        }, {
            value: 'portNum',
            type: 'number',
            allowSymbol: ['>', '<', '>=', '<='],
        }, {
            value: 'isAddToMappingView',
            type: 'boolean',
            $default: '=',
            allowSymbol: ['='],
        },
    ],
};
const LogicPortGrammar = {
    'lif.getBatch': [
        {
            value: 'NAME',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
            index: 2,
        },
        {
            value: 'vstoreName',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
        }, {
            value: 'RUNNINGSTATUS',
            type: 'enum',
            $default: '=',
            enumValue: { 0: '未知', 1: '正常', 2: '运行', 10: '已连接', 11: '未连接', 33: '待恢复', 53: '初始化中', 110: '待工作' },
            allowSymbol: ['=', '!='],
        }, {
            value: 'OPERATIONALSTATUS',
            type: 'boolean',
            $default: '=',
            map: ['=', ':'],
            allowSymbol: ['='],
        }, {
            value: 'SUPPORTPROTOCOL',
            type: 'enum',
            $default: '=',
            enumValue: {
                ...(SWITCH.SUPPORT_NAS ?
                    {
                        0: '无', 1: 'NFS',
                        ...(FeatureConfig.IsHideCifsLif ? {} : { 2: 'CIFS' }),
                        ...(FeatureConfig.IsHideNfsAndCifsLif ? {} : { 3: 'NFS+CIFS' }),
                        ...(FeatureConfig.IsHideIscsiLif ? {} : { 4: 'iSCSI' }),
                        8: 'FC/FCoE',
                        ...(FeatureConfig.IsHideNVMEOverRoCELif ? {} : { 64: 'NVMe_over_RoCE' }),
                        512: 'BGP',
                    } :
                    {
                        0: '无',
                        ...(FeatureConfig.IsHideNVMEOverRoCELif ? {} : { 64: 'NVMe_over_RoCE' }),
                    }),
                ...(ProductConfig.isSupportS3Protocol ? {
                    [LifProtoCol.S3]: 'S3',
                } : {}),
            },
            allowSymbol: ['=', '!='],
        }, {
            value: 'IPV4ADDR',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
        }, {
            value: 'IPV4MASK',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
        }, {
            value: 'IPV4GATEWAY',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
        }, {
            value: 'IPV6ADDR',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
        }, {
            value: 'IPV6MASK',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
        }, {
            value: 'IPV6GATEWAY',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
        }, {
            value: 'HOMEPORTNAME',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
        }, {
            value: 'CURRENTPORTNAME',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
        },
    ],
    // 微存储屏蔽性能监控
    ...(FeatureConfig.IsHidePms ? {} : performanceGrammar),
};
const VStoreGrammar = {
    'vstore.getBatch': [
        {
            value: 'NAME',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
            index: 2,
        }, {
            value: 'RUNNINGSTATUS',
            type: 'enum',
            $default: '=',
            enumValue: { 1: '正常', 53: '初始化中' },
            allowSymbol: ['=', '!='],
        }, {
            value: 'fileSystemCount',
            type: 'number',
            allowSymbol: ['>', '<', '>=', '<='],
        },
    ],
};
const LunSnapShotGrammar = {
    'snapshot.getSnapshot': [
        {
            value: 'NAME',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
            index: 2,
        }, {
            value: 'HEALTHSTATUS',
            type: 'enum',
            $default: '=',
            enumValue: { 1: '正常', 2: '故障', 15: '写保护' },
            allowSymbol: ['=', '!='],
        }, {
            value: 'RUNNINGSTATUS',
            type: 'enum',
            $default: '=',
            enumValue: { 43: '已激活', 44: '正在回滚', 45: '未激活' },
            allowSymbol: ['=', '!='],
        }, {
            value: 'SOURCELUNNAME',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
        }, {
            value: 'PARENTNAME',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
        }, {
            value: 'TIMESTAMP',
            type: 'date',
            allowSymbol: ['>', '<', '='],
            validator: (value): boolean => {
                const split = value.split('-');

                if (split.length !== 3 || !split[2]) {
                    throw new Error();
                }

                return moment(value).isValid();
            },
            tip: {
                timeSelect: true,
            },
        }, {
            value: 'snapCgName',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
        }, {
            value: 'WWN',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
        },
    ],
};
const FileSystemGrammar = {
    'fileSystem.getBatch': [
        {
            value: 'NAME',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
            index: 2,
        }, {
            value: 'vstoreName',
            type: 'string',
            map: ['=', ':'],
            $default: '=',
            allowSymbol: ['='],
        }, {
            value: 'CAPACITY',
            type: 'number',
            allowSymbol: ['>', '<', '>=', '<='],
            unit: 'Sector',
            tip: 'GB',
        }, {
            value: 'HEALTHSTATUS',
            type: 'enum',
            $default: '=',
            enumValue: { 1: '正常', 2: '故障', 14: '失效', 15: '写保护' },
            allowSymbol: ['=', '!='],
        }, {
            value: 'RUNNINGSTATUS',
            type: 'enum',
            $default: '=',
            enumValue: { 27: '在线', 28: '不在线', 35: '失效', 53: '初始化中' },
            allowSymbol: ['=', '!='],
        }, {
            value: 'dtreeCount',
            type: 'number',
            allowSymbol: ['>', '<', '>=', '<='],
        },
    ],
    ...fileSystemPerformanceGrammar,
};
export const grammar = {
    LUN: LunGrammar,
    LUNs: LunGrammar,
    LUNGroups: LunGroupGrammar,
    LUN组: LunGroupGrammar,
    Namespace: LunGrammar,
    Namespaces: LunGrammar,
    NamespaceGroups: LunGroupGrammar,
    Namespace组: LunGroupGrammar,
    Hosts: HostGrammar,
    主机: HostGrammar,
    HostGroups: HostGroupGrammar,
    主机组: HostGroupGrammar,
    PortGroups: PortGroupGrammar,
    端口组: PortGroupGrammar,
    LogicalPorts: LogicPortGrammar,
    逻辑端口: LogicPortGrammar,
    vStores: VStoreGrammar,
    多租户: VStoreGrammar,
    LUNSnapshots: LunSnapShotGrammar,
    LUN快照: LunSnapShotGrammar,
    FileSystems: FileSystemGrammar,
    文件系统: FileSystemGrammar,
};

// 将中文类型对应成对应的英文形式
export const mapType = (type: string): string => {
    const reg = new RegExp(`^${type}$`, 'i');
    for (const key of Object.keys(directions)) {
        if (reg.test(key)) {
            return directions[key];
        }
    }
    return type.toUpperCase();
};
export const mapTypeChinese = (type: string): string => {
    const reg = new RegExp(`^${type}$`, 'i');
    for (const key of Object.keys(directions)) {
        if (reg.test(directions[key])) {
            return key;
        }
    }
    return type.toUpperCase();
};
export const IgnoreCaseMatch = (key, obj): any[] => {
    const reg = new RegExp(`^${key}$`, 'i');
    const match = Object.keys(obj).find((k) => reg.test(k));
    return match ? [obj[match], match] : [null, undefined];
};

export const batch = (gLA, glB): GrammarList => {
    const TYPES = Object.keys(glB);
    const batchResult = {};
    TYPES.forEach((type) => {
        const apiB = glB[type];
        const apiA = gLA[type];
        batchResult[type] = assign(apiA, apiB);
    });
    return batchResult;
};
